import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Card,
  Drawer,
  Group,
  Progress,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconAlertTriangle, IconInfoCircle, IconSearch } from "@tabler/icons-react";
import { fetchBrandSources } from "./mentionsApi";

/**
 * Where the answers come from, and which of those places are not carrying you.
 *
 * The ranked list on the detection page answers "who gets read". This answers the question a brand
 * actually has, which is "so where do we publish" — and that turns on a number the flat list does
 * not show: how often the brand appears in the answers a source feeds. A domain cited a hundred
 * times whose answers never name you is an opportunity; one that always names you is already work
 * that paid off.
 */

const pct = (v) => `${Math.round(v * 100)}%`;

/** Rising, falling, or new — stated in words, because an arrow alone reads as precision. */
function Movement({ current, previous }) {
  if (previous === 0 && current > 0) {
    return (
      <Badge size="xs" variant="light" color="blue">
        new
      </Badge>
    );
  }
  if (previous === 0) return null;

  const change = (current - previous) / previous;
  // Under a fifth either way is noise at these counts, and labelling it invites reading a trend
  // into ordinary week-to-week movement.
  if (Math.abs(change) < 0.2) return null;

  return (
    <Badge size="xs" variant="light" color={change > 0 ? "teal" : "orange"}>
      {change > 0 ? "▲" : "▼"} {pct(Math.abs(change))}
    </Badge>
  );
}

function SourceRow({ source, totalAnswers }) {
  const [open, setOpen] = useState(false);
  const missing = source.citations - source.withBrand;

  return (
    <Card
      withBorder
      padding="sm"
      onClick={() => setOpen((o) => !o)}
      style={{ cursor: "pointer" }}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text fz="sm" fw={600} truncate>
              {source.domain}
            </Text>
            {source.isOwnSite && (
              <Badge size="xs" variant="light" color="gray">
                your site
              </Badge>
            )}
            <Movement current={source.citations} previous={source.previousCitations} />
          </Group>

          <Text fz="xs" c="dimmed">
            Cited in {source.citations} of {totalAnswers} answers
            {" · "}
            {source.prompts} prompt{source.prompts === 1 ? "" : "s"}
            {" · "}
            you appear in {source.withBrand}
          </Text>

          {/* The bar is the point of the row: filled is where you are already present, the rest
              is the gap. */}
          <Progress.Root size="sm">
            <Progress.Section
              value={source.citations === 0 ? 0 : (source.withBrand / source.citations) * 100}
              color="teal"
            />
            <Progress.Section
              value={source.citations === 0 ? 0 : (missing / source.citations) * 100}
              color="gray"
            />
          </Progress.Root>
        </Stack>

        <Text fz="lg" fw={700} lh={1.2}>
          {pct(source.shareOfAnswers)}
        </Text>
      </Group>

      {open && (
        <Stack gap="xs" mt="sm">
          {source.pages?.length > 0 ? (
            <>
              <Text fz="xs" fw={600}>
                Pages being read
              </Text>
              <Stack gap={2}>
                {source.pages.map((page) => (
                  <Anchor
                    key={page}
                    href={page}
                    target="_blank"
                    rel="noopener noreferrer"
                    fz="xs"
                    style={{ overflowWrap: "anywhere" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {page}
                  </Anchor>
                ))}
              </Stack>
            </>
          ) : (
            <Text fz="xs" c="dimmed">
              No individual pages recorded yet. Pages are kept from checks taken after this was
              added, so an older domain shows its count without them.
            </Text>
          )}

          {source.promptTexts?.length > 0 && (
            <>
              <Text fz="xs" fw={600} mt={4}>
                Cited when answering
              </Text>
              <Stack gap={2}>
                {source.promptTexts.slice(0, 8).map((prompt) => (
                  <Text key={prompt} fz="xs" c="dimmed" lineClamp={1}>
                    {prompt}
                  </Text>
                ))}
              </Stack>
            </>
          )}

          <Text fz="xs" c="dimmed" mt={4}>
            Last cited {new Date(source.lastSeen).toLocaleDateString()}
          </Text>
        </Stack>
      )}
    </Card>
  );
}

export default function SourceExplorerDrawer({ brandId, days, opened, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    if (!opened || !brandId) return;
    setData(null);
    setLoading(true);
    setError(null);
    try {
      setData(await fetchBrandSources(brandId, { days: Number(days) }));
    } catch (e) {
      setError(e?.message ?? "The sources could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [brandId, days, opened]);

  useEffect(() => {
    load();
  }, [load]);

  const needle = filter.trim().toLowerCase();
  const sources = (data?.sources ?? []).filter(
    (s) => !needle || s.domain.toLowerCase().includes(needle),
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="min(760px, 96vw)"
      title={<Title order={4}>Where the answers come from</Title>}
    >
      <Stack gap="md">
        <Text fz="sm" c="dimmed">
          Every site cited in answers to prompts that do not name you, over the last {days} days.
          The share is how many of those answers used it; the bar shows how often you were named in
          them. A source read constantly that never names you is where to publish next.
        </Text>

        {loading && <Skeleton h={240} radius="md" />}

        {error && (
          <Alert color="error" icon={<IconAlertTriangle size={18} />} title="Could not load sources">
            {error}
          </Alert>
        )}

        {data && data.totalAnswers === 0 && (
          <Alert color="info" icon={<IconInfoCircle size={18} />}>
            No unaided answers in this window yet, so nothing has been cited.
          </Alert>
        )}

        {data && data.sources.length > 0 && (
          <>
            <TextInput
              placeholder="Filter by domain"
              leftSection={<IconSearch size={14} />}
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
            />
            <Text fz="xs" c="dimmed">
              {sources.length} of {data.sources.length} sources · {data.totalAnswers} answers
            </Text>
            {sources.map((s) => (
              <SourceRow key={s.domain} source={s} totalAnswers={data.totalAnswers} />
            ))}
          </>
        )}
      </Stack>
    </Drawer>
  );
}
